import { createContext, useContext, useState } from 'react';

import { useIsSupportMagazine } from './Magazines.hooks';
import styles from './Magazines.module.css';
import config from '../../../config';
import { NAVBAR_ICON_URL, Issue } from '../../../constants';
import { HomeContext } from '../Home.context';
import { useIssue } from '../../issue/Issue.hooks';

import { useIssues } from '../../../hooks/use-issue';

import { Modal } from '../../../components/modal/Modal';

import { Loading } from '../../../components/loading/Loading';
import { TemplatePoets } from '../../../config.type';
import { TrackingContext } from '../../../tracking/Tracking.context';
import { Link } from '@tanstack/react-router';

export const Magazines = () => {
	const [isSubscriptionDialogOpen, setIsSubscriptionDialogOpen] = useState(
		false,
	);
	const isDigitalAccess = useIsSupportMagazine();
	const { selectedIssue } = useContext(HomeContext);
	const { issues, isLoading } = useIssues();
	// useSelectIssue(issues);
	/*useEffect(() => {
		if (!selectedIssue) {
			document.title = config.build.title;
			return;
		}
		if (!selectedIssue.name) {
			return;
		}
		document.title = selectedIssue.name;
	}, [selectedIssue]);*/

	if (isLoading) {
		return (
			<section
				className={[styles['magazines'], styles['loading']].join(' ')}
			>
				<MagazineLoading />
			</section>
		);
	}
	if (selectedIssue) {
		return (
			<section
				className={styles['magazines']}
				style={{ width: '100%', height: '100%' }}
			>
				<CoverIssue />
			</section>
		);
	}

	return (
		<MagazinesContext.Provider
			value={{
				isDigitalAccess,
				setIsSubscriptionDialogOpen,
			}}
		>
			<section className={styles['magazines']}>
				<Banner />
				<IssuesContainer issues={issues} />
				<SubscritionDialog isOpen={isSubscriptionDialogOpen} />
			</section>
		</MagazinesContext.Provider>
	);
};

const MagazinesContext = createContext<MagazinesContextProps>({
	isDigitalAccess: null,
	setIsSubscriptionDialogOpen: () => {},
});

interface MagazinesContextProps {
	isDigitalAccess: boolean | null;
	setIsSubscriptionDialogOpen: any;
}

function Banner() {
	const [isLoaded, setIsLoaded] = useState(false);
	setTimeout(() => {
		setIsLoaded(true);
	}, 10000);
	return (
		<div className={styles['banner']}>
			<ins
				data-revive-zoneid="63"
				data-revive-id="fbf717dc1969dc79e517b3994cd0df44"
			></ins>
			{isLoaded ? (
				<script
					async
					src="https://fast.poets-writers.org/openx/www/delivery/asyncjs.php"
				></script>
			) : null}
		</div>
	);
}

export const IssuesContainer = ({ issues }: IssuesProps) => {
	if (!issues) {
		return null;
	}
	const latestIssue = issues[0];
	return (
		<div className={styles['issues-container']}>
			<div>
				{latestIssue ? <LatestIssue issue={latestIssue} /> : null}
			</div>
			<div>
				<Issues issues={issues} />
			</div>
		</div>
	);
};

const LatestIssue = ({ issue }: { issue: Issue }) => {
	const { sendEvent } = useContext(TrackingContext);
	const { isDigitalAccess, setIsSubscriptionDialogOpen } = useContext(
		MagazinesContext,
	);
	if (issue?.description) {
		issue.description = ``;
	}
	const { id, name, cover, description, slug } = issue;
	const contentProtection = config?.app?.contentProtection;
	const template = config.app.template as TemplatePoets;
	const url = template?.config?.skipTOC ? `/issues/$id` : `/preview/$id`;
	const from = `${window.location.pathname}?section=magazines`;

	const imageComponent = isDigitalAccess ? (
		<Link
			to={url}
			params={{
				id: slug ?? id,
			}}
			from={from}
			onClick={() => {
				sendEvent({
					type: 'BTN_CLICK_ISSUE',
					data: {
						issue: {
							id: parseInt(`${issue.id}`, 10),
							slug: issue.slug,
						},
					},
				});
			}}
		>
			<figure role="button" style={{ cursor: 'pointer' }}>
				{contentProtection ? (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={340}
						className="content-protection"
						onContextMenu={e => e.preventDefault()}
					/>
				) : (
					<img src={cover} alt={`${name} - Cover`} width={340} />
				)}
			</figure>
		</Link>
	) : (
		// eslint-disable-next-line jsx-a11y/anchor-is-valid
		<a
			href="#"
			onClick={() => {
				setIsSubscriptionDialogOpen(true);
			}}
		>
			<figure role="button" style={{ cursor: 'pointer' }}>
				{contentProtection ? (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={340}
						className="content-protection"
						onContextMenu={e => e.preventDefault()}
					/>
				) : (
					<img src={cover} alt={`${name} - Cover`} width={340} />
				)}
			</figure>
		</a>
	);

	return (
		<div className={styles['latest-issue']}>
			{imageComponent}

			<div className={styles['title']}>
				<h1>{name}</h1>
				{description ? <p>{description}</p> : null}
				<hr />
			</div>
		</div>
	);
};

const Issues = ({ issues }: IssuesProps) => {
	// TODO Remove this line
	const newIssues = issues;
	return (
		<div className={styles['issues']}>
			{newIssues.map(issue => (
				<IssueComponent key={issue.id} issue={issue} />
			))}
		</div>
	);
};

interface IssuesProps {
	issues: Array<Issue>;
}

const CoverIssue = () => {
	const { isDigitalAccess, setIsSubscriptionDialogOpen } = useContext(
		MagazinesContext,
	);
	const { selectedIssue } = useContext(HomeContext);
	const { issue, isLoading } = useIssue(`${selectedIssue?.id}`);

	if (isLoading || !issue) {
		return (
			<div
				style={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					width: '100%',
					height: '100%',
					padding: 0,
				}}
			>
				<MagazineLoading />
			</div>
		);
	}
	const { id, name, articles, cover, description, slug } = issue;

	const filteredArticles = articles ? articles.filter(onlyIncludedToc) : [];

	const from = `${window.location.pathname}?section=magazines&id=${id}`;
	const onContentProtection = config?.app?.contentProtection;

	const imageComponent = isDigitalAccess ? (
		<Link
			to={`/issues/$id`}
			params={{
				id: slug ?? id,
			}}
			from={from}
			className={styles['image']}
		>
			{onContentProtection ? (
				<img
					src={cover}
					alt={`${name} - Cover`}
					className="content-protection"
					onContextMenu={e => e.preventDefault()}
				/>
			) : (
				<img src={cover} alt={`${name} - Cover`} />
			)}
		</Link>
	) : (
		// eslint-disable-next-line jsx-a11y/anchor-is-valid
		<a
			href="#"
			onClick={() => {
				setIsSubscriptionDialogOpen(true);
			}}
		>
			<figure role="button" style={{ cursor: 'pointer' }}>
				{onContentProtection ? (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={340}
						className="content-protection"
						onContextMenu={e => e.preventDefault()}
					/>
				) : (
					<img src={cover} alt={`${name} - Cover`} width={340} />
				)}
			</figure>
		</a>
	);
	return (
		<div className={styles['cover']}>
			<div>
				{imageComponent}
				<div className={styles['title']}>
					<h1>{name}</h1>
					{description ? <p>{description}</p> : null}
					<hr />
				</div>
			</div>
			<div>
				<ul className={styles['toc']}>
					{filteredArticles.map((article: any) => (
						<li key={article.id}>
							<Link
								to={`/issues/$id/articleId`}
								from={from}
								params={{
									id: id,
									articleId: article.id,
								}}
							>
								<h3>{article.name['en']}</h3>
								{article.description ? (
									<small>{article.description}</small>
								) : null}
							</Link>
						</li>
					))}
					<div className={styles['offer']}>
						<a
							href="https://www.pw.org/about-us/advertise/display"
							target="_blank"
							rel="noreferrer"
						>
							<img alt="Offer" src="/assets/test.png" />
						</a>
					</div>
				</ul>
			</div>
		</div>
	);
};

/*Exclude articles that are excluded from being display in Toc*/
function onlyIncludedToc(article: any) {
	if (article.role === 'advert') {
		return false;
	}
	return article.channel === null || article?.channel?.toc === 'included';
}

const IssueComponent = ({ issue }: IssueProps) => {
	const { sendEvent } = useContext(TrackingContext);
	const { isDigitalAccess, setIsSubscriptionDialogOpen } = useContext(
		MagazinesContext,
	);
	if (!issue.description) {
		issue.description = ``;
	}
	const { id, name, description, cover, slug } = issue;
	const template = config.app.template as TemplatePoets;
	const url = template?.config?.skipTOC ? `/issues/$id` : `/preview/$id`;
	const from = `${window.location.pathname}?section=magazine`;
	const contentProtection = config?.app?.contentProtection;

	const imageComponent = isDigitalAccess ? (
		<Link
			to={url}
			params={{
				id: slug ?? id,
			}}
			from={from}
			onClick={() => {
				sendEvent({
					type: 'BTN_CLICK_ISSUE',
					data: {
						issue: {
							id: parseInt(`${issue.id}`, 10),
							slug: issue.slug,
						},
					},
				});
			}}
		>
			<figure role="button" style={{ cursor: 'pointer' }}>
				{contentProtection ? (
					<img
						src={cover}
						alt={`${name} - cover`}
						className="content-protection"
						onContextMenu={e => e.preventDefault()}
						width={340}
					/>
				) : (
					<img src={cover} alt={`${name} - cover`} width={340} />
				)}
			</figure>
		</Link>
	) : (
		// eslint-disable-next-line jsx-a11y/anchor-is-valid
		<a
			href="#"
			onClick={() => {
				setIsSubscriptionDialogOpen(true);
			}}
		>
			<figure role="button" style={{ cursor: 'pointer' }}>
				{contentProtection ? (
					<img
						src={cover}
						alt={`${name} - Cover`}
						width={340}
						className="content-protection"
						onContextMenu={e => e.preventDefault()}
					/>
				) : (
					<img src={cover} alt={`${name} - Cover`} width={340} />
				)}
			</figure>
		</a>
	);
	const title = <h4>{name}</h4>;
	return (
		<div className={styles['issue']}>
			<div>{imageComponent}</div>
			<div>
				{title}
				<p>{description}</p>
			</div>
		</div>
	);
};

interface IssueProps {
	issue: Issue;
}

const MagazineLoading = () => {
	return (
		<div className={styles['loading']}>
			<Loading />
		</div>
	);
};

const SubscritionDialog: React.FC<SubscritionDialogProps> = ({
	isOpen,
}: SubscritionDialogProps) => {
	const { setIsSubscriptionDialogOpen } = useContext(MagazinesContext);

	const { subscriptionUrl, title } = config.build;
	return (
		<Modal
			hasCloseBtn={true}
			isOpen={isOpen}
			onClose={() => {
				setIsSubscriptionDialogOpen(false);
			}}
		>
			<div className={styles['subscription-dialog']}>
				<img src={NAVBAR_ICON_URL} alt="logo" />
				<h3>Subscriber access required</h3>
				<p>
					Subscribe to the digital edition of{' '}
					<em>{title} Magazine</em> to access this content.
				</p>
				{subscriptionUrl ? (
					<button
						type="button"
						onClick={() => {
							open(subscriptionUrl, 'blank');
						}}
					>
						Subscribe Now
					</button>
				) : null}
			</div>
		</Modal>
	);
};

interface SubscritionDialogProps {
	isOpen: boolean;
}
